import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { Box, useMediaQuery, useTheme, Button, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import ApiService from "../../../config/api";
import { propertyObject } from "../../../models/property";
import { validatePropertyForm } from "../../../models/propertyValidation";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ArrowBack as ArrowBackIcon, } from '@mui/icons-material';

const EditPropertyForm = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const [localities, setLocalities] = useState([]);
  const toggleDrawer = () => {
    setSidebarOpen(!sidebarOpen);
  };
  const [property, setProperty] = useState({
    ...propertyObject,
    locality: "",
    projectStatus: "",
    onDemand: ""
  });
  const [errorMessages, setErrorMessages] = useState({});
  const [nearbyPlace, setNearbyPlace] = useState("");
  const [amenity, setAmenity] = useState("");
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageGet, setImageGet] = useState([]);
  const [brochureFile, setBrochureFile] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const predefinedNearbyPlaces = [
    "Shopping Mall",
    "Hospital",
    "School",
    "Public Transport",
    "Restaurant",
    "Bank",
    "Supermarket",
    "Park",
    "Gym",
    "Cinema",
  ];

  // Fetch localities on component mount
  useEffect(() => {
    const fetchLocalities = async () => {
      try {
        const res = await ApiService.get("localities");
        if (!res.data.error) {
          setLocalities(res.data.meta);
        }
      } catch (err) {
        console.error("Error fetching localities:", err);
        toast.error("Failed to load localities");
      }
    };
    fetchLocalities();
  }, []);

  useEffect(() => {
    if (id) {
      fetchPropertyDetails(id);
    }
  }, [id]);

  const fetchPropertyDetails = async (id) => {
    try {
      const successResponse = await ApiService.get(`admin/property/${id}`);
      const { meta: { property } } = successResponse.data;
      setProperty({
        ...property,
        locality: property.locality?._id || "",
        projectStatus: property.projectStatus || "",
      });
      setImageGet(property.images || []);
      setImagePreviews(property.images || []);
    } catch (error) {
      console.error("Error fetching property details:", error);
      toast.error("Failed to load property details");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const isNumericField =
      name.includes("number") ||
      ["price", "discountPrice", "floor", "area", "latitude", "longitude", "builtInArea", "availableQuantity", "totalQuantity"].includes(name);

    const newValue = isNumericField ? Math.max(0, Number(value)) : value;

    setProperty((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "projectStatus" && value !== "completed" ? { onDemand: false } : {}),
    }));

    if (errorMessages[name] && value.trim()) {
      setErrorMessages((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };


  const handleBrochureChange = (e) => {
    setBrochureFile(e.target.files[0]);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => file);
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setProperty((prevProperty) => ({
      ...prevProperty,
      images: [...newImages],
    }));
    setImagePreviews([...newPreviews]);
  };

  const handleRemoveServerImage = (image) => {
    setImageGet((prevImages) => prevImages.filter((img) => img !== image));
  };

  const handleRemoveNewImage = (index) => {
    setImagePreviews((prevPreviews) => prevPreviews.filter((_, i) => i !== index));
    setProperty((prevProperty) => ({
      ...prevProperty,
      images: prevProperty.images.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveAmenity = (index) => {
    setProperty((prevProperty) => ({
      ...prevProperty,
      amenities: prevProperty.amenities.filter((_, i) => i !== index),
    }));
  };

  const handleRemovePlace = (index) => {
    setProperty((prevProperty) => ({
      ...prevProperty,
      nearbyPlaces: prevProperty.nearbyPlaces.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { isValid, errors } = validatePropertyForm({
      ...property,
      locality: property.locality,
      projectStatus: property.projectStatus,
    });

    if (!isValid) {
      setErrorMessages(errors);
      toast.error("Please fill all required fields correctly");
      return;
    }

    const formData = new FormData();

    Object.keys(property).forEach((key) => {
      if (key !== "images" && key !== "brochure") {
        const value = property[key];
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return;
        }
        if (key === "amenities" && Array.isArray(property.amenities)) {
          property.amenities.forEach((amenity, index) => {
            formData.append(`amenities[${index}]`, amenity);
          });
        } else if (key === "nearbyPlaces" && Array.isArray(property.nearbyPlaces)) {
          property.nearbyPlaces.forEach((place, index) => {
            formData.append(`nearbyPlaces[${index}]`, place);
          });
        } else {
          formData.append(key, value); // Append locality as the ID
        }
      }
    });

    const serverImages = Array.isArray(imageGet) ? imageGet : [];
    const newImages = Array.isArray(property.images) ? property.images : [];

    newImages.forEach((image) => {
      if (image instanceof File) {
        formData.append("images", image);
      }
    });

    serverImages.forEach((image) => {
      if (typeof image === "string") {
        formData.append("imageUrls", image);
      }
    });

    if (brochureFile instanceof File) {
      formData.append("brochure", brochureFile);
    } else if (property.brochure && typeof property.brochure === "string") {
      formData.append("brochureUrl", property.brochure);
    }

    try {
      const response = await ApiService.put(`admin/update-property/${id}`, formData);
      toast.success(response.data.message);
      alert(response.data.message);
      navigate("/admin/adminPropertyListing");
    } catch (error) {
      console.error("Error updating property:", error);
      const errMsg = error?.response?.data?.message || "Error updating property";
      toast.error(errMsg);
    }
  };

  return (
    <Box
      display="grid"
      gridTemplateColumns={sidebarOpen ? "200px 1fr" : "60px 1fr"}
      sx={{
        transition: "grid-template-columns 0.3s ease-in-out",
        overflow: "hidden",
      }}
    >
      <Sidebar
        open={sidebarOpen}
        toggleDrawer={toggleDrawer}
        setOpen={setSidebarOpen}
      />
      <div className="property-cont">
        <div className="card rounded card-add-property g-2">
          <div
            className="px-3 py-3"
            style={{
              borderBottom: "1px solid var(--bs-secondary)",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                color: "#D6AD60"
              }}
            >
            </Button>
            <h5 className="mb-0 p-2">Edit Property</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <input
                    placeholder="Enter Title"
                    className={`customInput form-control ${errorMessages.title ? "is-invalid" : ""}`}
                    type="text"
                    id="title"
                    name="title"
                    value={property.title}
                    onChange={handleChange}
                  />
                  {errorMessages.title && (
                    <p className="error-text">{errorMessages.title}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <textarea
                    className={`form-control ${errorMessages.description ? "is-invalid" : ""}`}
                    id="description"
                    name="description"
                    rows="3"
                    value={property.description}
                    onChange={handleChange}
                  ></textarea>
                  {errorMessages.description && (
                    <p className="error-text">{errorMessages.description}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="price" className="form-label">
                    Price
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.price ? "is-invalid" : ""}`}
                    type="number"
                    id="price"
                    name="price"
                    value={property.price}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.price && (
                    <p className="error-text">{errorMessages.price}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="projectStatus" className="form-label">
                    Project Status
                  </label>
                  <select
                    className={`customInput form-control ${errorMessages.projectStatus ? "is-invalid" : ""}`}
                    id="projectStatus"
                    name="projectStatus"
                    value={property.projectStatus}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Select Project Status
                    </option>
                    <option value="upcoming">Upcoming</option>
                    <option value="completed">Completed</option>
                  </select>
                  {errorMessages.projectStatus && (
                    <p className="error-text">{errorMessages.projectStatus}</p>
                  )}
                </div>


                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="locality" className="form-label">
                    Locality
                  </label>
                  <select
                    className={`customInput form-control ${errorMessages.locality ? "is-invalid" : ""}`}
                    id="locality"
                    name="locality"
                    value={property.locality}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Select Locality
                    </option>
                    {localities.map((locality) => (
                      <option key={locality._id} value={locality._id}>
                        {locality.name}
                      </option>
                    ))}
                  </select>
                  {errorMessages.locality && (
                    <p className="error-text">{errorMessages.locality}</p>
                  )}
                </div>

                {property.projectStatus === "completed" && (
                  <div className="col-md-4 col-sm-6 px-4 mt-1">
                    <FormControl>
                      <FormLabel
                        id="on-demand-radio-label"
                        className="form-label"
                        style={{ color: "#D6AD60" }}
                      >
                        On Demand
                      </FormLabel>

                      <RadioGroup
                        row
                        aria-labelledby="on-demand-radio-label"
                        name="onDemand"
                        value={property.onDemand ? "true" : "false"} // controlled
                        onChange={(e) =>
                          setProperty({
                            ...property,
                            onDemand: e.target.value === "true", // boolean
                          })
                        }
                      >
                        <FormControlLabel
                          value="true"
                          control={
                            <Radio
                              sx={{
                                color: "#D6AD60",
                                "&.Mui-checked": { color: "#D6AD60" },
                              }}
                            />
                          }
                          label="Yes"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              color: "inherit",
                            },
                            "&.Mui-checked .MuiFormControlLabel-label": {
                              color: "#D6AD60",
                            },
                          }}
                        />

                        <FormControlLabel
                          value="false"
                          control={
                            <Radio
                              sx={{
                                color: "#D6AD60",
                                "&.Mui-checked": { color: "#D6AD60" },
                              }}
                            />
                          }
                          label="No"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              color: "inherit",
                            },
                            "&.Mui-checked .MuiFormControlLabel-label": {
                              color: "#D6AD60",
                            },
                          }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                )}


                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="category" className="form-label">
                    Category
                  </label>
                  <select
                    className={`customInput form-control ${errorMessages.category ? "is-invalid" : ""}`}
                    id="category"
                    name="category"
                    value={property.category || ""}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Select Category
                    </option>
                    <option value="DUPLEX">Duplex</option>
                    <option value="APARTMENT">Apartment</option>
                    <option value="COMMERCIAL">Commercial</option>
                    <option value="RESIDENTIAL">Residential</option>
                    <option value="PLOT">Plot/Land</option>
                    <option value="VILLA">Villa</option>
                  </select>
                  {errorMessages.category && (
                    <p className="error-text">{errorMessages.category}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="builtInArea" className="form-label">
                    Build In Area
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.builtInArea ? "is-invalid" : ""}`}
                    type="number"
                    id="builtInArea"
                    name="builtInArea"
                    value={property.builtInArea}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.builtInArea && (
                    <p className="error-text">{errorMessages.builtInArea}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="area" className="form-label">
                    Carpet Area
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.area ? "is-invalid" : ""}`}
                    type="number"
                    id="area"
                    name="area"
                    value={property.area}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.area && (
                    <p className="error-text">{errorMessages.area}</p>
                  )}
                </div>

                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="totalQuantity" className="form-label">
                    Total Quantity
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.totalQuantity ? "is-invalid" : ""}`}
                    type="number"
                    id="totalQuantity"
                    name="totalQuantity"
                    value={property.totalQuantity}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.totalQuantity && (
                    <p className="error-text">{errorMessages.totalQuantity}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="availableQuantity" className="form-label">
                    Available Quantity
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.availableQuantity ? "is-invalid" : ""}`}
                    type="number"
                    id="availableQuantity"
                    name="availableQuantity"
                    value={property.availableQuantity}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.availableQuantity && (
                    <p className="error-text">{errorMessages.availableQuantity}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="discountPrice" className="form-label">
                    Discount Price
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.discountPrice ? "is-invalid" : ""}`}
                    type="number"
                    id="discountPrice"
                    name="discountPrice"
                    value={property.discountPrice}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.discountPrice && (
                    <p className="error-text">{errorMessages.discountPrice}</p>
                  )}
                </div>


                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="floor" className="form-label">
                    Floor
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.floor ? "is-invalid" : ""}`}
                    type="number"
                    id="floor"
                    name="floor"
                    value={property.floor}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.floor && (
                    <p className="error-text">{errorMessages.floor}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="city" className="form-label">
                    City
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.city ? "is-invalid" : ""}`}
                    type="text"
                    id="city"
                    name="city"
                    value={property.city}
                    onChange={handleChange}
                  />
                  {errorMessages.city && (
                    <p className="error-text">{errorMessages.city}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="state" className="form-label">
                    State
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.state ? "is-invalid" : ""}`}
                    type="text"
                    id="state"
                    name="state"
                    value={property.state}
                    onChange={handleChange}
                  />
                  {errorMessages.state && (
                    <p className="error-text">{errorMessages.state}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="country" className="form-label">
                    Country
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.country ? "is-invalid" : ""}`}
                    type="text"
                    id="country"
                    name="country"
                    value={property.country}
                    onChange={handleChange}
                  />
                  {errorMessages.country && (
                    <p className="error-text">{errorMessages.country}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="pincode" className="form-label">
                    Pincode
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.pincode ? "is-invalid" : ""}`}
                    type="text"
                    id="pincode"
                    name="pincode"
                    value={property.pincode}
                    onChange={handleChange}
                  />
                  {errorMessages.pincode && (
                    <p className="error-text">{errorMessages.pincode}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="latitude" className="form-label">
                    Latitude
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.latitude ? "is-invalid" : ""}`}
                    type="number"
                    id="latitude"
                    name="latitude"
                    value={property.latitude}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.latitude && (
                    <p className="error-text">{errorMessages.latitude}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="longitude" className="form-label">
                    Longitude
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.longitude ? "is-invalid" : ""}`}
                    type="number"
                    id="longitude"
                    name="longitude"
                    value={property.longitude}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.longitude && (
                    <p className="error-text">{errorMessages.longitude}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="numberOfBedrooms" className="form-label">
                    Number of Bedrooms
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.numberOfBedrooms ? "is-invalid" : ""}`}
                    type="number"
                    id="numberOfBedrooms"
                    name="numberOfBedrooms"
                    value={property.numberOfBedrooms}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.numberOfBedrooms && (
                    <p className="error-text">{errorMessages.numberOfBedrooms}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="numberOfBathrooms" className="form-label">
                    Number of Bathrooms
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.numberOfBathrooms ? "is-invalid" : ""}`}
                    type="number"
                    id="numberOfBathrooms"
                    name="numberOfBathrooms"
                    value={property.numberOfBathrooms}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.numberOfBathrooms && (
                    <p className="error-text">{errorMessages.numberOfBathrooms}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="constructionYear" className="form-label">
                    Construction Year
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.constructionYear ? "is-invalid" : ""}`}
                    type="number"
                    id="constructionYear"
                    name="constructionYear"
                    value={property.constructionYear}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.constructionYear && (
                    <p className="error-text">{errorMessages.constructionYear}</p>
                  )}
                </div>
                {/* <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="agentName" className="form-label">
                    Agent Name
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.agentName ? "is-invalid" : ""}`}
                    type="text"
                    id="agentName"
                    name="agentName"
                    value={property.agentName}
                    onChange={handleChange}
                  />
                  {errorMessages.agentName && (
                    <p className="error-text">{errorMessages.agentName}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="agentPhone" className="form-label">
                    Agent Phone
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.agentPhone ? "is-invalid" : ""}`}
                    type="number"
                    id="agentPhone"
                    name="agentPhone"
                    value={property.agentPhone}
                    onChange={handleChange}
                    onWheel={(e) => e.target.blur()}
                  />
                  {errorMessages.agentPhone && (
                    <p className="error-text">{errorMessages.agentPhone}</p>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label htmlFor="agentEmail" className="form-label">
                    Agent Email
                  </label>
                  <input
                    className={`customInput form-control ${errorMessages.agentEmail ? "is-invalid" : ""}`}
                    type="email"
                    id="agentEmail"
                    name="agentEmail"
                    value={property.agentEmail}
                    onChange={handleChange}
                  />
                  {errorMessages.agentEmail && (
                    <p className="error-text">{errorMessages.agentEmail}</p>
                  )}
                </div> */}
                <div className="col-md-4 col-sm-6 px-4 mb-3 list-color">
                  <label htmlFor="amenity" className="form-label">
                    Add Amenity
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={amenity}
                      onChange={(e) => setAmenity(e.target.value)}
                    />
                    <button
                      className="btn btn-add btn-outline-secondary"
                      type="button"
                      onClick={() => {
                        const amenitiesToAdd = amenity
                          .split(",")
                          .map((item) => item.trim())
                          .filter((item) => item && !property.amenities.includes(item));
                        if (amenitiesToAdd.length) {
                          setProperty((prevProperty) => ({
                            ...prevProperty,
                            amenities: [...prevProperty.amenities, ...amenitiesToAdd],
                          }));
                          setAmenity("");
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <ul>
                    {property.amenities.map((amenityItem, index) => (
                      <li key={index} style={{ color: "var(--bs-accent)" }}>
                        {amenityItem}
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAmenity(index)}
                          aria-label="remove"
                          style={{ marginLeft: "10px" }}
                        >
                          <CloseIcon
                            style={{ color: "var(--bs-accent)", fontSize: "17px" }}
                          />
                        </IconButton>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3 list-color">
                  <label htmlFor="nearbyPlace" className="form-label">
                    Add Nearby Place
                  </label>
                  <div className="input-group mb-3">
                    <input
                      list="placesList"
                      className="form-control"
                      placeholder="Type or select nearby places"
                      value={nearbyPlace}
                      onChange={(e) => setNearbyPlace(e.target.value)}
                    />
                    <datalist id="placesList">
                      {predefinedNearbyPlaces.map((placeOption, index) => (
                        <option key={index} value={placeOption} />
                      ))}
                    </datalist>
                    <button
                      className="btn btn-add btn-outline-secondary"
                      type="button"
                      onClick={() => {
                        const placesList = nearbyPlace
                          .split(",")
                          .map((item) => item.trim())
                          .filter((item) => item && !property.nearbyPlaces.includes(item));
                        if (placesList.length) {
                          setProperty((prevProperty) => ({
                            ...prevProperty,
                            nearbyPlaces: [...prevProperty.nearbyPlaces, ...placesList],
                          }));
                          setNearbyPlace("");
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <ul>
                    {property.nearbyPlaces.map((placeItem, index) => (
                      <li key={index}>
                        {placeItem}
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePlace(index)}
                          aria-label="remove"
                          style={{ marginLeft: "10px" }}
                        >
                          <CloseIcon
                            style={{ color: "var(--bs-accent)", fontSize: "17px" }}
                          />
                        </IconButton>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label className="form-label">Upload Brochure</label>
                  <input
                    className="form-control"
                    type="file"
                    accept="application/pdf"
                    onChange={handleBrochureChange}
                  />
                  {property?.brochure && (
                    <a
                      href={property.brochure}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary mt-2"
                    >
                      View Existing Brochure
                    </a>
                  )}
                </div>
                <div className="col-md-4 col-sm-6 px-4 mb-3">
                  <label className="form-label">Upload Images</label>
                  <input
                    className="form-control"
                    type="file"
                    multiple
                    accept="image/png, image/jpeg"
                    onChange={handleFileChange}
                  />
                  <div className="mt-2">
                    {imageGet.map((image, index) => (
                      <div
                        key={index}
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginRight: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <img
                          src={image}
                          alt={`Server Image ${index}`}
                          style={{ maxWidth: "100px" }}
                        />
                        <p
                          className="clear-img"
                          onClick={() => handleRemoveServerImage(image)}
                        >
                          x
                        </p>
                      </div>
                    ))}
                    {imagePreviews.map((preview, index) => (
                      <div
                        key={index}
                        style={{
                          position: "relative",
                          display: "inline-block",
                          marginRight: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <img
                          src={preview}
                          alt={`New Image Preview ${index}`}
                          style={{ maxWidth: "100px" }}
                        />
                        <p
                          className="clear-img"
                          onClick={() => handleRemoveNewImage(index)}
                        >
                          x
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-accent mx-3 my-2">
                Submit
              </button>
              <button
                className="btn btn-secondary mx-3 my-2"
                onClick={() => navigate(`/admin/adminPropertyListing`)}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </Box>
  );
};

export default EditPropertyForm;